import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  customerId: "",
  productId: "",
  question: "",
  answer: "",
  isActive: true,
  isRejected: false
});

export function ProductQuestionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const [cRes, pRes] = await Promise.all([apiGet("/api/Customers"), apiGet("/api/Products")]);
    if (cRes.ok) setCustomers(cRes.data);
    if (pRes.ok) setProducts(pRes.data);
    if (!cRes.ok) setError(cRes.error ?? "Failed to load customers");
    else if (!pRes.ok) setError(pRes.error ?? "Failed to load products");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/ProductQuestions/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product question");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        customerId: x.customerId ?? "",
        productId: x.productId ?? "",
        question: x.question ?? "",
        answer: x.answer ?? "",
        isActive: Boolean(x.isActive),
        isRejected: Boolean(x.isRejected)
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.customerId || !form.productId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductQuestions", {
      id: editingId ?? 0,
      customerId: Number(form.customerId),
      productId: Number(form.productId),
      question: form.question || null,
      answer: form.answer || null,
      isActive: Boolean(form.isActive),
      isRejected: Boolean(form.isRejected)
    });
    if (!res.ok) setError(res.error ?? "Failed to save question");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductQuestions/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product question");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        customerId: x.customerId ?? "",
        productId: x.productId ?? "",
        question: x.question ?? "",
        answer: x.answer ?? "",
        isActive: Boolean(x.isActive),
        isRejected: Boolean(x.isRejected)
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Product Question" : "Add Product Question"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-questions-form"
            disabled={saving}
            title="Save"
            aria-label="Save product question"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-questions-list" className="icon-button icon-button--toolbar" title="Product questions list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-questions-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Customer</span>
          <select value={form.customerId} onChange={(e) => setForm((p) => ({ ...p, customerId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select customer</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || `Customer #${c.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Product</span>
          <select value={form.productId} onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `Product #${p.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Question</span>
          <textarea rows={2} value={form.question} onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))} />
        </label>
        <label className="field">
          <span>Answer</span>
          <textarea rows={2} value={form.answer} onChange={(e) => setForm((p) => ({ ...p, answer: e.target.value }))} />
        </label>
        <label className="field checkbox-field">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
          <span>Is Active</span>
        </label>
        <label className="field checkbox-field">
          <input type="checkbox" checked={form.isRejected} onChange={(e) => setForm((p) => ({ ...p, isRejected: e.target.checked }))} />
          <span>Is Rejected</span>
        </label>
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
